import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@/theme/ThemeContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { useDebouncedEffect } from "@/hooks/useDebouncedEffect";
import { useToast } from "@/components/Toast";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import Skeleton from "@/components/Skeleton";
import Editor from "@/components/Editor";
import {
  listPacks, getPackFile, upsertPackFile, deletePackFile, deletePack,
  listPackAssets, uploadPackAsset, deletePackAsset, type PackAsset,
} from "@/api/packs";
import type { GamePack } from "@/types";

/** PackEditor — édite un pack de jeu (P1.5).
 *  Arbre de fichiers à gauche, Monaco à droite. Autosave sur changement (debounce 700 ms).
 *  Réservé aux profs du cours (l'API renvoie 403 sinon). */
export default function PackEditor() {
  const { courseId: courseIdParam, packId: packIdParam } = useParams<{ courseId: string; packId: string }>();
  const courseId = Number(courseIdParam);
  const packId   = Number(packIdParam);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const confirm = useConfirm();
  const toast   = useToast();

  const [pack, setPack]               = useState<GamePack | null | undefined>(undefined);
  const [activePath, setActivePath]   = useState<string | null>(null);
  const [content, setContent]         = useState<string>("");
  const [loading, setLoading]         = useState(false);
  const [newName, setNewName]         = useState("");

  // Charge la liste de fichiers du pack via listPacks (le détail inclut déjà les paths).
  const reloadPack = () =>
    listPacks(courseId)
      .then((packs) => setPack(packs.find((p) => p.id === packId) ?? null))
      .catch(() => setPack(null));
  useEffect(() => { reloadPack(); }, [courseId, packId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sélectionne le 1er fichier par défaut quand le pack se charge.
  useEffect(() => {
    if (pack && pack.files.length > 0 && activePath === null) {
      setActivePath(pack.files[0]);
    }
  }, [pack, activePath]);

  // Charge le contenu du fichier actif.
  useEffect(() => {
    if (!activePath) { setContent(""); return; }
    setLoading(true);
    getPackFile(packId, activePath)
      .then((f) => setContent(f.content))
      .catch(() => toast.error(`Erreur de chargement de ${activePath}`))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath, packId]);

  // Autosave : 700 ms après la dernière frappe.
  useDebouncedEffect(() => {
    if (!activePath) return;
    upsertPackFile(packId, activePath, content)
      .catch(() => toast.error("Échec de la sauvegarde"));
  }, [content, activePath]);

  const createFile = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await upsertPackFile(packId, name, "");
      setNewName("");
      await reloadPack();
      setActivePath(name);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Création refusée (nom invalide ?)");
    }
  };

  const removeFile = async () => {
    if (!activePath) return;
    const ok = await confirm({
      title: `Supprimer « ${activePath} » ?`,
      message: "Le fichier sera définitivement supprimé du pack. Cette action est irréversible.",
      confirmLabel: "Supprimer", danger: true,
    });
    if (!ok) return;
    await deletePackFile(packId, activePath);
    setActivePath(null);
    await reloadPack();
  };

  const removePack = async () => {
    if (!pack) return;
    const ok = await confirm({
      title: `Supprimer le pack « ${pack.name} » ?`,
      message: "Tous les fichiers du pack seront effacés. Les étapes qui le référencent verront leur lien rompu (mais leur scaffold reste intact).",
      confirmLabel: "Supprimer", danger: true,
    });
    if (!ok) return;
    await deletePack(packId);
    navigate(`/instructor/courses/${courseId}`);
  };

  if (pack === undefined) {
    return <div style={styles.wrap}><Skeleton height={32} width="40%" /><Skeleton height={400} /></div>;
  }
  if (pack === null) {
    return (
      <div style={styles.wrap}>
        <Link to={`/instructor/courses/${courseId}`} style={styles.back}><Icon name="back" size={16} /> Retour au cours</Link>
        <p style={styles.empty}>Pack introuvable.</p>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <style>{`
        .pl-pack-file { background: transparent; }
        .pl-pack-file:hover { background: var(--bg-bar); }
        .pl-pack-file.is-active { background: var(--badge-bg); color: var(--blue); font-weight: 700; }
      `}</style>

      <Link to={`/instructor/courses/${courseId}`} style={styles.back}>
        <Icon name="back" size={16} /> Retour au cours
      </Link>

      <div style={styles.header}>
        <Icon name="panel" size={20} style={{ color: "var(--blue)" }} />
        <h1 style={styles.title}>Pack <code style={styles.code}>{pack.name}</code></h1>
        <span style={{ flex: 1 }} />
        <Button variant="secondary" size="sm" onClick={removePack}>
          <Icon name="trash" size={14} /> Supprimer le pack
        </Button>
      </div>

      <p style={styles.hint}>
        Édite ici les fichiers Python du pack. À l'exécution côté élève, ils sont montés dans
        <code> /lib/pymods/{pack.name}/</code> → le scaffold peut <code>import {pack.name}</code>.
      </p>

      <div style={styles.editor}>
        {/* Liste de fichiers */}
        <aside style={styles.fileList}>
          <div style={styles.fileListHead}>Fichiers</div>
          {pack.files.length === 0 && <p style={styles.fileEmpty}>Aucun fichier — crées-en un ci-dessous.</p>}
          {pack.files.map((p) => (
            <button key={p}
                    type="button"
                    className={`pl-pack-file ${p === activePath ? "is-active" : ""}`}
                    style={styles.fileBtn}
                    onClick={() => setActivePath(p)}>
              <Icon name="file" size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
              <span style={styles.fileName}>{p}</span>
            </button>
          ))}
          <div style={styles.fileNew}>
            <input style={styles.fileInput}
                   placeholder="ex. scenes.py"
                   value={newName}
                   onChange={(e) => setNewName(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") createFile(); }} />
            <Button variant="secondary" size="sm" onClick={createFile} disabled={!newName.trim()}>+</Button>
          </div>
        </aside>

        {/* Monaco */}
        <main style={styles.codePane}>
          {activePath === null ? (
            <div style={styles.placeholder}>Sélectionne un fichier ou crées-en un.</div>
          ) : loading ? (
            <Skeleton height="100%" />
          ) : (
            <>
              <div style={styles.codeHead}>
                <code style={styles.code}>{activePath}</code>
                <span style={styles.savedHint}>Sauvegarde automatique</span>
                <span style={{ flex: 1 }} />
                <Button variant="secondary" size="sm" onClick={removeFile}>
                  <Icon name="trash" size={13} /> Supprimer
                </Button>
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <Editor value={content} onChange={setContent}
                        theme={theme === "light" ? "vs" : "vs-dark"} />
              </div>
            </>
          )}
        </main>
      </div>

      <AssetsSection packId={packId} packName={pack.name} />
    </div>
  );
}


/** Section assets binaires (Sprint 5.C bonus) — PNG/JPG/GIF/SVG/WebP/MP3/OGG/WAV.
 *  Drag-and-drop ou bouton « + Asset ». Le path est dérivé du nom du fichier
 *  (ex. `hero.png`) ; si l'asset existe déjà, le backend overwrite atomique +
 *  nouveau content_hash → cache PWA invalidé via le `?v=hash` côté gamelib. */
function AssetsSection({ packId, packName }: { packId: number; packName: string }) {
  const toast    = useToast();
  const confirm  = useConfirm();
  const inputRef = useRef<HTMLInputElement>(null);
  const [assets, setAssets] = useState<PackAsset[] | null>(null);
  const [dragOn, setDragOn] = useState(false);
  const [busy,   setBusy]   = useState(false);

  const reload = () =>
    listPackAssets(packId).then(setAssets).catch(() => setAssets([]));
  useEffect(() => { reload(); }, [packId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file: File, customPath?: string) => {
    setBusy(true);
    try {
      const a = await uploadPackAsset(packId, file, customPath);
      const existed = assets?.some((x) => x.path === a.path);
      toast.success(existed ? `« ${a.path} » remplacé.` : `« ${a.path} » ajouté.`);
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Échec de l'upload.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleFiles = (files: FileList | null) => {
    const f = files?.[0];
    if (f) upload(f);
  };

  const remove = async (asset: PackAsset) => {
    const ok = await confirm({
      title: `Supprimer « ${asset.path} » ?`,
      message: "Le fichier sera retiré du pack et purgé du disque. Les leçons "
             + "qui pointent vers cet asset afficheront un damier magenta tant "
             + "qu'il n'est pas remplacé.",
      confirmLabel: "Supprimer", danger: true,
    });
    if (!ok) return;
    try {
      await deletePackAsset(packId, asset.id);
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Échec de la suppression.");
    }
  };

  return (
    <section style={styles.assetsSection}>
      <div style={styles.assetsHead}>
        <Icon name="file" size={16} style={{ color: "var(--blue)" }} />
        <h2 style={styles.assetsTitle}>Assets binaires</h2>
      </div>
      <p style={styles.hint}>
        Sprites PNG/JPG/SVG/WebP et sons MP3/OGG/WAV. À l'usage dans un scaffold :
        <code> await precharge_image("hero", asset_url("hero.png"))</code> puis
        <code> cree_image("hero", "hero", …)</code>. Re-déposer un fichier au
        même nom remplace son contenu (le cache PWA est invalidé automatiquement
        via le hash dans l'URL).
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOn(true); }}
        onDragLeave={() => setDragOn(false)}
        onDrop={(e) => { e.preventDefault(); setDragOn(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        style={{
          ...styles.dropzone,
          ...(dragOn ? styles.dropzoneOn : {}),
          ...(busy   ? styles.dropzoneBusy : {}),
        }}
        role="button"
        aria-label="Glisse un fichier image ou audio, ou clique pour parcourir"
      >
        <Icon name="file" size={18} style={{ color: "var(--blue)" }} />
        <span style={styles.dropzoneText}>
          {busy ? "Upload en cours…" : dragOn ? "Relâche pour téléverser" : "Glisse une image (PNG, JPG, SVG…) ou un son ici, ou clique pour parcourir"}
        </span>
        <input ref={inputRef} type="file"
               accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp,audio/mpeg,audio/ogg,audio/wav"
               style={{ display: "none" }}
               onChange={(e) => handleFiles(e.target.files)} />
      </div>

      {assets === null ? (
        <Skeleton height={60} />
      ) : assets.length === 0 ? (
        <p style={styles.fileEmpty}>Aucun asset binaire pour ce pack.</p>
      ) : (
        <div style={styles.assetList}>
          {assets.map((a) => (
            <div key={a.id} style={styles.assetRow}>
              {a.mime_type.startsWith("image/") && (
                <img src={`/api/exercises/packs/${packId}/assets/${a.path}?v=${a.content_hash}`}
                     alt={a.path}
                     style={styles.assetThumb} />
              )}
              <div style={styles.assetMeta}>
                <code style={styles.assetPath}>{a.path}</code>
                <span style={styles.assetSub}>
                  {a.mime_type} · {formatSize(a.size_bytes)} · {a.content_hash.slice(0, 8)}…
                </span>
              </div>
              <span style={{ flex: 1 }} />
              <button type="button"
                      style={styles.assetUrlBtn}
                      title={`Copie l'URL versionnée — utilise dans le scaffold via asset_url("${a.path}")`}
                      onClick={() => {
                        const url = `asset_url("${a.path}")`;
                        navigator.clipboard?.writeText(url).then(
                          () => toast.success(`Copié : ${url}`),
                          () => toast.error("Copie impossible."),
                        );
                      }}>
                <Icon name="duplicate" size={13} /> asset_url
              </button>
              <button type="button"
                      style={styles.assetDelBtn}
                      onClick={() => remove(a)}
                      title={`Supprimer ${a.path}`}>
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <p style={styles.assetsFooterHint}>
        Pack référencé via <code>{packName}</code>. Les URLs servies aux élèves
        ressemblent à <code>/api/exercises/packs/{packId}/assets/&lt;path&gt;?v=&lt;hash&gt;</code>
        et sont cachées par le service worker (cf. règle PWA <code>exercise-assets-v1</code>).
      </p>
    </section>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { display: "flex", flexDirection: "column", gap: 14, maxWidth: 1200 },
  back: { display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-muted)", textDecoration: "none", fontSize: 13, fontWeight: 600 },
  header: { display: "flex", alignItems: "center", gap: 10 },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: "var(--text)" },
  code: { fontFamily: "var(--font-mono)", fontSize: 15, background: "var(--bg-bar)", padding: "1px 6px", borderRadius: "var(--radius-sm)", color: "var(--blue)" },
  hint: { margin: 0, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 },
  empty: { color: "var(--text-faint)", fontSize: 13.5, fontStyle: "italic" },
  editor: { display: "grid", gridTemplateColumns: "240px 1fr", gap: 14, minHeight: 480, border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)", overflow: "hidden", background: "var(--bg-panel)" },
  fileList: { display: "flex", flexDirection: "column", padding: 10, gap: 2, background: "var(--bg-bar)", borderRight: "1px solid var(--border-soft)" },
  fileListHead: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)", padding: "4px 6px 8px" },
  fileBtn: { display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", border: "none", borderRadius: "var(--radius-sm)", color: "var(--text)", padding: "6px 8px", cursor: "pointer", fontSize: 13, fontFamily: "var(--font-mono)" },
  fileName: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  fileEmpty: { color: "var(--text-faint)", fontSize: 12.5, fontStyle: "italic", padding: "6px 8px", margin: 0 },
  fileNew: { display: "flex", gap: 6, marginTop: 8 },
  fileInput: { flex: 1, minWidth: 0, background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 12, padding: "5px 8px", fontFamily: "var(--font-mono)" },
  codePane: { display: "flex", flexDirection: "column", minWidth: 0 },
  codeHead: { display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--border-soft)" },
  savedHint: { fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" },
  placeholder: { display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--text-faint)", fontSize: 14 },

  // Assets binaires (Sprint 5.C bonus)
  assetsSection: { display: "flex", flexDirection: "column", gap: 10, marginTop: 18, padding: "16px 18px", background: "var(--bg-panel)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)" },
  assetsHead:    { display: "flex", alignItems: "center", gap: 10 },
  assetsTitle:   { margin: 0, fontSize: 17, fontWeight: 600, color: "var(--text)" },
  dropzone:      { display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "18px 16px", border: "2px dashed var(--border)", borderRadius: "var(--radius)", background: "var(--bg-base)", cursor: "pointer", color: "var(--text-muted)", fontSize: 13 },
  dropzoneOn:    { borderColor: "var(--blue)", background: "var(--badge-bg)", color: "var(--blue)" },
  dropzoneBusy:  { opacity: 0.6, cursor: "progress" },
  dropzoneText:  { fontWeight: 600 },
  assetList:     { display: "flex", flexDirection: "column", gap: 6 },
  assetRow:      { display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border-soft)", background: "var(--bg-bar)" },
  assetThumb:    { width: 36, height: 36, objectFit: "contain", background: "repeating-conic-gradient(#222 0% 25%, #2c2c2c 0% 50%) 50% / 12px 12px", borderRadius: 4, flexShrink: 0 },
  assetMeta:     { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
  assetPath:     { fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  assetSub:      { fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" },
  assetUrlBtn:   { display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text-muted)", padding: "4px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  assetDelBtn:   { background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--red)", padding: "5px 8px", cursor: "pointer", display: "inline-flex" },
  assetsFooterHint: { margin: 0, fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 },
};
